/* global femcare, axios */

/**
 * Manages CSRF protection and standardized AJAX form submission for all .ajax-form elements.
 * Forms must include a hidden input with name="formId".
 */
femcare.form.protection = {
    /**
     * Initializes CSRF protection and sets up AJAX submission for all forms with the 'ajax-form' class.
     * Reuses tokens from sessionStorage based on formId to avoid redundant API calls.
     */
    init() {
        this.updatePaywallVisibility();

        const storedTokens = JSON.parse(sessionStorage.getItem('csrfTokens')) || [];

        document.querySelectorAll('form.ajax-form').forEach((form) => {
            const formIdInput = form.querySelector('input[name="formId"]');

            if (!formIdInput) {
                return;
            }

            const formId = formIdInput.value;

            if (this.isFormAlreadyProtected(form, formId, storedTokens)) {
                const existingToken = form.getAttribute('data-token');
                this.setupSubmitHandler(form, existingToken);
                return;
            }

            this.configFormProtection(form, formId, storedTokens);
        });
    },

    /**
     * Shows or hides the paywall modal based on cookie value.
     */
    updatePaywallVisibility() {
        const paywallNewsletter = document.querySelector('.modal.paywall');
        if (paywallNewsletter) {
            if (this.hasNewsletterSignup()) {
                paywallNewsletter.classList.remove('displayed');
            } else {
                paywallNewsletter.classList.add('displayed');
            }
        }
    },

    /**
     * Checks if a token for the given formId is already stored in sessionStorage.
     * If so, sets the data-token attribute on the form.
     *
     * @param {HTMLElement} form - The form element.
     * @param {string} formId - The ID of the form.
     * @param {Array} storedTokens - Array of existing CSRF token entries.
     * @returns {boolean} - True if token was reused, false otherwise.
     */
    isFormAlreadyProtected(form, formId, storedTokens) {
        const tokenObj = storedTokens.find((entry) => entry.formId === formId);
        if (tokenObj) {
            form.setAttribute('data-token', tokenObj.token);

            return true;
        }
        return false;
    },

    /**
     * Fetches a CSRF token and stores it in sessionStorage, then sets up the submit handler.
     *
     * @param {HTMLElement} form - The form to protect.
     * @param {string} formId - The form's ID.
     * @param {Array} storedTokens - Previously stored tokens in sessionStorage.
     */
    configFormProtection(form, formId, storedTokens) {
        this.provideCSRFToken()
            .then((token) => {
                if (!storedTokens.some((entry) => entry.token === token)) {
                    storedTokens.push({ token, formId });
                    sessionStorage.setItem('csrfTokens', JSON.stringify(storedTokens));
                }

                form.setAttribute('data-token', token);
                this.setupSubmitHandler(form, token);
            })
            .catch((error) => this.handleError(error));
    },

    /**
     * Requests a new CSRF token from the backend.
     *
     * @returns {Promise<string>} - Resolves with the token string.
     */
    async provideCSRFToken() {
        const response = await axios.get(`${femcare.shared.util.baseUrl}ajax/core/shared/generatetoken`, {
            headers: {
                'X-Disable-Output-Caching': 'true',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });
        return response.data;
    },

    /**
     * Submits the form via Axios with CSRF headers and handles server response.
     * This function prevents the default submit behavior and sends the form via AJAX.
     * If the server response indicates a redirect, it navigates to the new URL.
     * If the response contains validation errors, it updates the DOM accordingly.
     * Adds newsletter-specific behavior:
     * If the form is a newsletter form, set a 'newsletter_signup=true' cookie on success.
     *
     * @param {HTMLElement} form - The form element to bind.
     * @param {string} token - CSRF token to include in request headers.
     */
    setupSubmitHandler(form, token) {
        form.addEventListener('submit', (e) => {
            e.preventDefault();

            const $form = $(form);
            if (!$form.validateForm()) {
                return;
            }

            const formData = new FormData(form);
            const method = form.getAttribute('method').toLowerCase();
            const url = form.getAttribute('action');
            const submitButton = form.querySelector('button[type="submit"]');

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.classList.add('btn-loading');
            }

            axios({
                method,
                url,
                data: method === 'post' ? formData : null,
                headers: {
                    'X-Request-Verification-Token': token,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                validateStatus: () => true // allow handling all status codes manually
            })
                .then((response) => {
                    const data = response.data;

                    // Status 200 with error
                    if (data && data.error) {
                        this.handleError({ response: { data } });

                        return;
                    }

                    // Redirect on 302 or if redirectUrl is present in 200 OK
                    if (response.status === 302 || data?.redirectUrl) {
                        // Newsletter cookie on success even if server triggers redirect explicitly
                        if (this.isNewsletterForm(form) && data && data.success === true) {
                            document.cookie = 'newsletter_signup=true';
                            this.updatePaywallVisibility();
                        }

                        window.location.href = data.redirectUrl || response.request.responseURL;
                        return;
                    }

                    const parent = form.parentElement;

                    // Successful response with content replacement
                    if (data && data.success && data.validationPassed) {
                        if (this.isNewsletterForm(form)) {
                            document.cookie = 'newsletter_signup=true';
                            this.updatePaywallVisibility();
                        }
                        if (data.content) {
                            parent.innerHTML = data.content;
                        }

                        return;
                    }

                    // Validation errors
                    if (data && !data.validationPassed && Array.isArray(data.validationMessages)) {
                        data.validationMessages.forEach(({ field, message }) => {
                            const input = document.getElementById(field);
                            if (!input) {
                                return;
                            }

                            const formGroup = input.closest('.form-group');

                            if (formGroup) {
                                formGroup.classList.remove('success');
                                formGroup.classList.add('has-error');

                                const validationErrorField = formGroup.querySelector('.validation-error span');

                                if (validationErrorField) {
                                    validationErrorField.textContent = message;
                                }
                            }

                            input.setAttribute('aria-invalid', 'true');
                        });
                    }
                })
                .catch((error) => this.handleError(error))
                .finally(() => {
                    if (submitButton) {
                        submitButton.disabled = false;
                        submitButton.classList.remove('btn-loading');
                    }
                });
        });
    },

    /**
     * Determines whether a form is the newsletter subscription form.
     *
     * @param {HTMLElement} form
     * @returns {boolean}
     */
    isNewsletterForm(form) {
        if (form.classList.contains('newsletter-subscription')) {
            return true;
        }

        if (form.closest('.newsletter-subscription')) {
            return true;
        }

        return false;
    },

    /**
     * Handles global form submission errors (e.g., CSRF failure or server error).
     * Updates the modal text dynamically with the error message from backend.
     *
     * @param {Object|string} error - Axios error object or plain message string
     */
    handleError(error) {
        const alertModal = document.querySelector('.modal--alert');
        const defaultTitle = 'Submission Failed';
        const defaultMessage = 'There has been an error submitting the form. Please, try again later.';

        let title = defaultTitle;
        let errorMessage = defaultMessage;

        if (typeof error === 'object' && error?.response?.data?.error) {
            const errorObject = error.response.data.error;
            if (typeof errorObject.heading === 'string') {
                title = errorObject.heading;
            }
            if (typeof errorObject.message === 'string') {
                errorMessage = errorObject.message;
            }
        }

        if (typeof error === 'string') {
            errorMessage = error;
        }

        if (alertModal) {
            const alertModalTitle = alertModal.querySelector('.modal__title');
            const alertModalDescription = alertModal.querySelector('.modal__description');

            if (alertModalTitle) {
                alertModalTitle.textContent = title;
            }
            if (alertModalDescription) {
                alertModalDescription.textContent = errorMessage;
            }

            alertModal.classList.add('displayed');
            alertModal.focus();
        }
    },

    /**
     * Checks if the user is subscribed to the newsletter through a cookie value.
     *
     * @returns {boolean}
     */
    hasNewsletterSignup() {
        if (!document.cookie) {
            return false;
        }

        const cookies = document.cookie.split('; ').reduce((acc, curr) => {
            const [name, value] = curr.split('=');
            if (name) {
                acc[name] = decodeURIComponent(value);
            }
            return acc;
        }, {});

        return cookies['newsletter_signup'] === 'true';
    }
};
